import { createFileRoute } from "@tanstack/react-router";
import { Loader2, Plus, Save, Undo } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import { AssetUploadPanel } from "@/components/branding/AssetUploadPanel";
import { BrandCreateModal } from "@/components/branding/BrandCreateModal";
import { BrandDangerZoneCard } from "@/components/branding/BrandDangerZoneCard";
import { BrandPanelDefaultCard } from "@/components/branding/BrandPanelDefaultCard";
import { BrandPortalDomainCard } from "@/components/branding/BrandPortalDomainCard";
import { BrandSmtpConfigCard } from "@/components/branding/BrandSmtpConfigCard";
import { BrandPreview } from "@/components/branding/BrandPreview";
import { BrandSelector } from "@/components/branding/BrandSelector";
import { ThemeEditor } from "@/components/branding/ThemeEditor";
import { Button } from "@/components/ui/button";
import { useCurrentUser } from "@/lib/hooks/auth";
import { useBrandsList, useBrandUpdate } from "@/lib/hooks/branding";
import {
  DEFAULT_THEME,
  type BrandTheme,
  type BrandsListFilters,
} from "@/lib/schemas/branding";

const brandingSearchSchema = z.object({
  // `brand` = deep-link from global search palette (ADR-0091): preselect
  // the brand in the BrandSelector. Optional, free-form string.
  brand: z.string().optional(),
});

export const Route = createFileRoute("/_app/branding")({
  component: BrandingPage,
  validateSearch: brandingSearchSchema,
});

const DEFAULT_FILTERS: BrandsListFilters = { limit: 50 };

function BrandingPage(): JSX.Element {
  const { t } = useTranslation();
  const { brand: brandFromUrl } = Route.useSearch();
  const me = useCurrentUser();
  const query = useBrandsList(DEFAULT_FILTERS);
  const brands = useMemo(() => query.data?.items ?? [], [query.data]);

  const [selectedBrandId, setSelectedBrandId] = useState<string | undefined>(
    brandFromUrl,
  );
  const [draftTheme, setDraftTheme] = useState<BrandTheme>(DEFAULT_THEME);
  const [dirty, setDirty] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);

  // React to ?brand=... URL changes (palette navigates from the search).
  useEffect(() => {
    if (brandFromUrl) setSelectedBrandId(brandFromUrl);
  }, [brandFromUrl]);

  // Auto-select primo brand quando i brand caricano (se nessun ?brand= in URL).
  useEffect(() => {
    if (!selectedBrandId && brands.length > 0) {
      const first = brands[0];
      if (first) setSelectedBrandId(first.id);
    }
  }, [brands, selectedBrandId]);

  const selectedBrand = useMemo(
    () => brands.find((b) => b.id === selectedBrandId),
    [brands, selectedBrandId],
  );

  // Quando cambia brand, ricarica draft theme dal server
  useEffect(() => {
    if (selectedBrand) {
      setDraftTheme(selectedBrand.theme);
      setDirty(false);
    }
  }, [selectedBrand]);

  const updateMutation = useBrandUpdate(selectedBrandId);

  function onThemeChange(next: BrandTheme): void {
    setDraftTheme(next);
    setDirty(true);
  }

  function onReset(): void {
    if (selectedBrand) {
      setDraftTheme(selectedBrand.theme);
      setDirty(false);
    }
  }

  async function onSave(): Promise<void> {
    if (!selectedBrandId) return;
    try {
      await updateMutation.mutateAsync({ theme: draftTheme });
      setDirty(false);
    } catch {
      // surfaced via mutation.error below
    }
  }

  // ADR-0088: un reseller può personalizzare il brand solo se delegato dal suo
  // owner; altrimenti eredita quello dell'owner e non vede l'editor (mirror del
  // gate BE). admin/superuser sempre. Customer/peer non raggiungono la route.
  const meRole = me.data?.role;
  const canCustomize =
    meRole === "admin" ||
    meRole === "superuser" ||
    (meRole === "reseller" && me.data?.branding_delegated === true);

  if (me.data && !canCustomize) {
    return (
      <div className="space-y-4">
        <header>
          <h1 className="text-3xl font-semibold tracking-tight">{t("nav.branding")}</h1>
          <p className="text-sm text-muted-foreground">{t("branding.subtitle")}</p>
        </header>
        <div className="rounded-md border border-border bg-muted/30 p-4 text-sm text-muted-foreground">
          {t("branding.not_delegated_notice")}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight">
            {t("nav.branding")}
          </h1>
          <p className="text-sm text-muted-foreground">
            {t("branding.subtitle")}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <BrandSelector
            brands={brands}
            value={selectedBrandId}
            onChange={setSelectedBrandId}
            disabled={query.isLoading}
          />
          <Button
            type="button"
            onClick={() => setCreateOpen(true)}
            size="sm"
            className="gap-1.5"
          >
            <Plus className="size-4" aria-hidden="true" />
            {t("branding.create.button")}
          </Button>
        </div>
      </header>

      <BrandCreateModal
        open={createOpen}
        onOpenChange={setCreateOpen}
        onCreated={(b) => setSelectedBrandId(b.id)}
      />

      {query.error && (
        <div className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive">
          {t("branding.load_error")} — {query.error.message}
        </div>
      )}

      {/* Deep-link with ?brand=<id> but the brand is not in the loaded list:
          either it doesn't exist, or the caller can't access it.
          Without this guard the rest of the page renders nothing → blank page. */}
      {!query.isLoading && brands.length > 0 && selectedBrandId && !selectedBrand && (
        <div className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive">
          {t("branding.brand_not_found", { id: selectedBrandId })}
        </div>
      )}

      {!query.isLoading && brands.length === 0 && (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <div className="space-y-3 rounded-md border border-dashed border-border p-6">
            <h2 className="text-lg font-semibold">{t("branding.empty_title")}</h2>
            <p className="text-sm text-muted-foreground">
              {t("branding.empty_hint")}
            </p>
            <p className="text-sm text-muted-foreground">
              {t("branding.default_active_hint")}
            </p>
            <Button
              type="button"
              onClick={() => setCreateOpen(true)}
              className="gap-1.5"
            >
              <Plus className="size-4" aria-hidden="true" />
              {t("branding.create.button")}
            </Button>
          </div>
          <div className="space-y-2">
            <h3 className="text-sm font-semibold text-muted-foreground">
              {t("branding.default_preview_title")}
            </h3>
            <BrandPreview theme={DEFAULT_THEME} brandName="DPanel" logoUrl={null} />
            <p className="text-xs text-muted-foreground">
              {t("branding.default_preview_hint")}
            </p>
          </div>
        </div>
      )}

      {selectedBrand && (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          {/* Left: editor */}
          <div className="space-y-4 rounded-md border border-border bg-card p-4">
            <h2 className="text-lg font-semibold">{t("branding.theme.title")}</h2>
            <ThemeEditor value={draftTheme} onChange={onThemeChange} />

            <div className="flex items-center justify-end gap-2 border-t border-border pt-3">
              <Button
                type="button"
                variant="ghost"
                onClick={onReset}
                disabled={!dirty || updateMutation.isPending}
              >
                <Undo className="mr-1.5 size-4" aria-hidden="true" />
                {t("common.cancel")}
              </Button>
              <Button
                type="button"
                onClick={() => void onSave()}
                disabled={!dirty || updateMutation.isPending}
              >
                {updateMutation.isPending ? (
                  <Loader2 className="mr-1.5 size-4 animate-spin" aria-hidden="true" />
                ) : (
                  <Save className="mr-1.5 size-4" aria-hidden="true" />
                )}
                {t("common.save")}
              </Button>
            </div>
            {updateMutation.error && (
              <p className="text-xs text-destructive">
                {updateMutation.error.message}
              </p>
            )}
            {!dirty && !updateMutation.error && updateMutation.isSuccess && (
              <p className="text-xs text-success">
                {t("branding.theme.saved")}
              </p>
            )}

            <div className="space-y-2 border-t border-border pt-3">
              <h3 className="text-sm font-semibold">{t("branding.assets.title")}</h3>
              <AssetUploadPanel brand={selectedBrand} />
            </div>

            <BrandPortalDomainCard brandId={selectedBrand.id} />

            <BrandSmtpConfigCard brand={selectedBrand} />

            <BrandPanelDefaultCard brand={selectedBrand} />

            <BrandDangerZoneCard
              brand={selectedBrand}
              onDeleted={() => setSelectedBrandId(undefined)}
            />
          </div>

          {/* Right: live preview (sticky on lg+) */}
          <div className="space-y-2 lg:sticky lg:top-4 lg:self-start">
            <h2 className="text-lg font-semibold">{t("branding.preview.title")}</h2>
            <BrandPreview
              theme={draftTheme}
              brandName={selectedBrand.display_name ?? selectedBrand.name}
              logoUrl={selectedBrand.asset_urls.logo_url}
            />
            <p className="text-xs text-muted-foreground">
              {t("branding.preview.hint")}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
